/*!
 * modulators.js — sinOsc() and its family: a parameter that moves by itself
 * © 2026 Rob Canning — GPLv3
 *
 *     pan:sinOsc(0.4)                         a sine, 0.4 times a second, -1..1
 *     pan:sinOsc(0.4, rand(0, 1))             ... starting at a random point:
 *                                             each poly voice its own
 *     filter:{freq:sinOsc(0.1, 0, 200, 4000)} with a range: lo, hi
 *     amp:triOsc(2, 0, 0.05, 0.2)             a triangle
 *     freq:sawOsc(0.25, 0, 100, 400)          a rising ramp that jumps back
 *     pan:pulseOsc(1, 0, -1, 1, 0.25)         a pulse, a quarter of each cycle high
 *     amp:lfNoise(2, 0.02, 0.2)               wandering smoothly, 2 new points a second
 *     filter:{freq:lfStep(4, 300, 3000)}      jumping, 4 times a second
 *
 *   sinOsc / triOsc / sawOsc (rate, phase, lo, hi)
 *   pulseOsc (rate, phase, lo, hi, width)
 *   lfNoise / lfStep (rate, lo, hi)
 *
 * rate in Hz, phase 0..1 of a cycle, lo..hi the range (-1..1 if left out),
 * width 0..1 (0.5). Any of them can be rand(...) -- drawn once for each
 * voice, so a poly synth's voices move apart. The names are SuperCollider's
 * (SinOsc, LFTri, LFSaw, LFPulse, LFNoise1, LFNoise0).
 *
 * Each is a loop of one cycle (or, for the noises, a few hundred random
 * points) played into the parameter by the audio engine: smooth, exact, and
 * light, whatever the frame rate.
 */

import { evalMaybeRandom } from "./audioShared.js";
import { isModulator, modSpec as specOf, modWave } from "./modShapes.js";

/** The settings of a call, its rand() arguments drawn now (for each voice). */
export const modSpec = v => specOf(v, evalMaybeRandom);
export { isModulator };

/**
 * Play a modulator into `param`: its own value is set to 0 and the
 * modulator's lo..hi is all it gets. Returns { spec, stop() }.
 * `scale` maps lo..hi on the way (amp's tremolo: divided by its peak).
 */
export function attachModulator(param, v, { scale = 1, ctx = window.sharedAudioCtx } = {}) {
  const spec = v.shape ? v : modSpec(v);
  const [wave, perUnit] = modWave(spec);
  const buf = ctx.createBuffer(1, wave.length, ctx.sampleRate);
  buf.copyToChannel(wave, 0);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  // rate cycles (noise: points) a second, from a loop of perUnit samples
  src.playbackRate.value = spec.rate * perUnit / ctx.sampleRate;
  const depth = ctx.createGain();
  depth.gain.value = (spec.hi - spec.lo) / 2 * scale;
  const centre = ctx.createConstantSource();
  centre.offset.value = (spec.hi + spec.lo) / 2 * scale;
  src.connect(depth);
  const now = ctx.currentTime;
  try { param.cancelScheduledValues(now); param.setValueAtTime(0, now); } catch { /* fine */ }
  depth.connect(param);
  centre.connect(param);
  // the starting point: phase of a cycle into the loop
  const offset = spec.phase * buf.duration * (spec.shape.startsWith("lf") ? 0 : 1);
  src.start(now, offset);
  centre.start(now);
  let stopped = false;
  return {
    spec, raw: v,
    stop() {
      if (stopped) return;
      stopped = true;
      for (const n of [src, centre]) { try { n.stop(); } catch { /* done */ } }
      for (const n of [src, depth, centre]) { try { n.disconnect(); } catch { /* gone */ } }
    },
  };
}
